import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  forwardRef
} from '@angular/core';

import { NG_VALUE_ACCESSOR, ControlValueAccessor, ReactiveFormsModule, FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs'; // Add this import
import { CommonModule } from '@angular/common';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';

@Component({
  selector: 'app-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],

  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],

  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchBoxComponent),
      multi: true,
    },
  ]
})

  /**
   * A reusable search input component with clear functionality.
   * Implements ControlValueAccessor to work seamlessly with Angular forms.
   * 
   * @example
   * <app-search-box placeholder="Search..." (searchChange)="onSearch($event)"></app-search-box>
   * 
   * @example With ngModel
   * <app-search-box [(ngModel)]="searchTerm"></app-search-box>
   */
export class SearchBoxComponent extends BaseUnsubscribeComponent implements ControlValueAccessor {
  private destroy$ = new Subject<void>();
  @Input() placeholder: string = 'Search...';
  @Input() debounce: number = 0;
  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  searchControl = new FormControl('');
  showClear = false;

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() {
      super();
  }

  ngOnInit(): void {
    if (this.debounce > 0) {

      this.searchControl.valueChanges
        .pipe(
          debounceTime(this.debounce),
          distinctUntilChanged(),
          takeUntil(this.destroy$) // Now this will work
        )
        .subscribe(value => {
          const v = value || '';
          this.searchChange.emit(v);
          this.onChange(v);
        });

      // Instant UI update for the clear button
      this.searchControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          this.showClear = (value || '').length > 0;
        });
    } else {
      this.searchControl.valueChanges.subscribe(value => {
        const v = value || '';
        this.showClear = v.length > 0;
        this.searchChange.emit(v);
        this.onChange(v);
      });
    }
  }

  // Mandatory: Trigger the unsubscription
  override ngOnDestroy(): void {    
    this.destroy$.next();
    this.destroy$.complete();
    // If your base class also has ngOnDestroy logic, call it:
    super.ngOnDestroy();
  }


  clear() {
    this.searchControl.setValue('', { emitEvent: false });
    this.showClear = false;
    this.searchChange.emit('');
    this.onChange('');
    this.onTouched();
    this.searchInput.nativeElement.focus();
  }

  writeValue(value: string | null): void {
    this.searchControl.setValue(value || '', { emitEvent: false });
    this.showClear = !!value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled)
      this.searchControl.disable();
    else
      this.searchControl.enable();
  }
}
